import { useState, useEffect, useCallback, useRef } from 'react';
import { RecordingData } from './useMediaRecorder';
import { updateAutoPlayState } from '../api/recordings';
import useRetryFetch from './useRetryFetch';

export interface AutoPlayStateEntry {
  playedAt: string;
  playedBy?: string;
}

export interface AutoPlayState {
  [activityId: string]: {
    audio?: AutoPlayStateEntry;
    video?: AutoPlayStateEntry;
  };
}

interface UseAutoPlayProps {
  /** Current activity ID */
  activityId: string | null;
  /** Recordings with auto-play config from prescription additionalInfoRecordings */
  recordings?: {
    audio?: RecordingData;
    video?: RecordingData;
  };
  /** Auto-play state tracking which recordings have been played (for single mode) */
  autoPlayState?: AutoPlayState;
  /** Process ID for updating auto-play state */
  processId?: string;
  /** Whether the component is in read-only mode (e.g., design view) */
  isDesignMode?: boolean;
  /** Callback when auto-play state changes */
  onAutoPlayStateChange?: (state: AutoPlayState) => void;
}

interface UseAutoPlayReturn {
  /** Whether audio/video should start auto-playing */
  shouldAutoPlay: { audio: boolean; video: boolean };
  /** Whether audio/video is currently playing */
  isPlaying: { audio: boolean; video: boolean };
  /** Set playing state for a type */
  setPlaying: (type: 'audio' | 'video', playing: boolean) => void;
  /** Mark a recording as played (for single mode) */
  markAsPlayed: (type: 'audio' | 'video') => Promise<void>;
  /** Stop all auto-playing media */
  stopAll: () => void;
  /** Whether user has manually stopped auto-play this session */
  userStopped: boolean;
  /** Reset user stopped state (for when navigating to new activity) */
  resetUserStopped: () => void;
}

/**
 * Hook to manage auto-play behavior for activity recordings
 *
 * Handles:
 * - Determining when to auto-play based on settings and state
 * - Tracking playing state
 * - Marking recordings as played for single mode
 * - Allowing users to stop auto-play
 */
export function useAutoPlay({
  activityId,
  recordings,
  autoPlayState,
  processId,
  isDesignMode = false,
  onAutoPlayStateChange,
}: UseAutoPlayProps): UseAutoPlayReturn {
  const retryFetch = useRetryFetch();

  // Playing state
  const [isPlaying, setIsPlayingState] = useState<{ audio: boolean; video: boolean }>({
    audio: false,
    video: false,
  });

  // Track if user manually stopped auto-play this session
  const [userStopped, setUserStopped] = useState(false);

  // Track the last activity we processed to detect activity changes
  const lastActivityRef = useRef<string | null>(null);

  // Track local auto-play state updates (optimistic)
  const [localAutoPlayState, setLocalAutoPlayState] = useState<AutoPlayState>({});

  // Merge local and server auto-play state
  const mergedAutoPlayState = { ...autoPlayState, ...localAutoPlayState };

  // Reset user stopped when activity changes
  useEffect(() => {
    if (activityId !== lastActivityRef.current) {
      lastActivityRef.current = activityId;
      setUserStopped(false);
      setIsPlayingState({ audio: false, video: false });
    }
  }, [activityId]);

  /**
   * Determine if a recording should auto-play
   */
  const shouldAutoPlayType = useCallback(
    (type: 'audio' | 'video'): boolean => {
      // Don't auto-play in design mode
      if (isDesignMode) return false;

      // Don't auto-play if no activity or recordings
      if (!activityId || !recordings) return false;

      const recording = recordings[type];
      if (!recording) return false;

      // Check if auto-play is enabled
      const autoPlayConfig = recording.autoPlay;
      if (!autoPlayConfig?.enabled) return false;

      // Don't auto-play if user manually stopped
      if (userStopped) return false;

      // For "always" mode, always play
      if (autoPlayConfig.mode === 'always') return true;

      // For "single" mode, check if already played
      const playedState = mergedAutoPlayState[activityId]?.[type];
      if (playedState?.playedAt) return false;

      return true;
    },
    [activityId, recordings, mergedAutoPlayState, userStopped, isDesignMode]
  );

  // Calculate shouldAutoPlay values
  const shouldAutoPlay = {
    audio: shouldAutoPlayType('audio'),
    video: shouldAutoPlayType('video'),
  };

  /**
   * Set playing state for a specific type
   */
  const setPlaying = useCallback((type: 'audio' | 'video', playing: boolean) => {
    setIsPlayingState((prev) => ({ ...prev, [type]: playing }));
  }, []);

  /**
   * Mark a recording as played (for single mode)
   */
  const markAsPlayed = useCallback(
    async (type: 'audio' | 'video') => {
      if (!activityId) return;

      const recording = recordings?.[type];
      if (!recording?.autoPlay?.enabled || recording.autoPlay.mode !== 'single') {
        return;
      }

      // Optimistically update local state
      const newState: AutoPlayState = {
        ...localAutoPlayState,
        [activityId]: {
          ...localAutoPlayState[activityId],
          [type]: { playedAt: new Date().toISOString() },
        },
      };
      setLocalAutoPlayState(newState);
      onAutoPlayStateChange?.(newState);

      // Persist to server if we have a processId
      if (processId) {
        try {
          await updateAutoPlayState(processId, activityId, type, retryFetch);
        } catch (error) {
          console.error('Failed to update auto-play state:', error);
          // Don't rollback - the recording was still played
        }
      }
    },
    [activityId, recordings, processId, localAutoPlayState, onAutoPlayStateChange, retryFetch]
  );

  /**
   * Stop all auto-playing media
   */
  const stopAll = useCallback(() => {
    setIsPlayingState({ audio: false, video: false });
    setUserStopped(true);
  }, []);

  /**
   * Reset user stopped state
   */
  const resetUserStopped = useCallback(() => {
    setUserStopped(false);
  }, []);

  return {
    shouldAutoPlay,
    isPlaying,
    setPlaying,
    markAsPlayed,
    stopAll,
    userStopped,
    resetUserStopped,
  };
}
